import { useState } from 'react'
import { Link, Outlet, useLocation } from 'react-router'
import { useTranslation } from 'react-i18next'
import { CancelApplicationButton } from '@/features/application-cancellation'
import { ResetApplicationButton } from '@/features/application-session'
import { HeaderActionSuppressionProvider } from '@/shared/components/headerActionVisibility'
import { SkipLink } from '@/shared/components/SkipLink'
import { paths } from '@/app/router/paths'
import logoUrl from '@/assets/vfs-global-logo.png'

/**
 * The page chrome from the Figma boards (nodes 2098:1915 / 2098:2418): a plain
 * white 112px header carrying the VFS lockup, the canvas, and a 72px footer of
 * policy links.
 *
 * The logo sits at x=120 in a 1440 frame, which is exactly (1440 - 1200) / 2 —
 * i.e. flush with the left edge of the 1200px content column, not inset from it.
 * So header, main and footer all share one `max-w-shell` wrapper and no element
 * needs an offset of its own.
 *
 * The POPIA line used to live here. Figma moves it onto the page bodies, under
 * the form card, where it is adjacent to the thing it is consenting about.
 *
 * Help moved out of the header for the same reason — the redesigned frames have
 * nothing to the right of the lockup. It is not deleted, because the header link
 * was the ONLY navigation to `/help`: the footer carries privacy, terms and
 * accessibility but never carried help, so removing it outright would ship a
 * reachable-by-URL-only page.
 *
 * One exception to "nothing to the right of the lockup": board 2173:30549 puts
 * an application-level action there once a reference number exists. WHICH action
 * depends on whether the application has been submitted, because the two are not
 * interchangeable:
 *
 *   - Mid-journey, the slot holds "Reset Application" — purely local, no network.
 *     Nothing has been submitted yet, so dropping the session IS the whole exit.
 *   - On the receipt, it holds "Cancel Application" (§8.1). Reset would be
 *     meaningless there (there is no progress left to clear, and the page offers
 *     "Start new application" of its own), while cancelling is the ONLY thing
 *     that still has an effect: it withdraws the application server-side. This is
 *     also the citizen's POPIA route to abandon an application they have already
 *     sent, so it must not be dropped.
 *
 * Route-scoped here rather than inside the buttons themselves, because they only
 * know about session state, and whether the header is the right place to show
 * them is a routing question — `/track` and the static pages keep an untouched
 * header even though a citizen's reference number is still sitting in session
 * storage.
 *
 * The pathname cannot carry the whole answer, though: the receipt shares its
 * route with the "Submitting Application" interstitial that precedes it, and the
 * slot must be EMPTY while §4.10 is in flight. The page says so itself through
 * `headerActionVisibility`, the same way it already suppresses the stepper.
 */
export function PublicLayout() {
  const { t } = useTranslation('common')
  const { pathname } = useLocation()

  const isWizardStep = pathname.startsWith('/apply/') && pathname !== paths.apply.idNumber

  // The receipt and the Submitting interstitial share this route. Matched on the
  // path rather than by importing SidebarLayout's step derivation, which is
  // private to that file.
  const isSubmitted = isWizardStep && pathname.endsWith('/submitted')

  // Reset carries the rule it had at the bottom of SidebarLayout: everywhere in
  // the journey EXCEPT the receipt, where Cancel takes the slot instead.
  const showReset = isWizardStep && !isSubmitted

  // Set by the submitting interstitial, which shares the receipt's route. It
  // outranks both flags below rather than being folded into them: "a submission
  // is on the wire" is a fact about the page, not about the URL.
  const [actionSuppressed, setActionSuppressed] = useState(false)

  return (
    <div className="flex min-h-dvh flex-col bg-page">
      <SkipLink />

      <header className="border-b border-hairline bg-white">
        <div className="mx-auto flex h-[72px] w-full max-w-shell items-center justify-between px-3 md:h-header">
          <Link to={paths.home} className="inline-flex rounded">
            {/* The asset is a 3x export (594x192); width/height carry the intrinsic
                size so the browser reserves the box before it decodes. */}
            <img
              src={logoUrl}
              alt={t('brand.logoAlt')}
              width={594}
              height={192}
              className="h-5 w-auto md:h-8"
            />
          </Link>
          {showReset && !actionSuppressed && <ResetApplicationButton />}
          {isSubmitted && !actionSuppressed && <CancelApplicationButton />}
        </div>
      </header>

      <main id="main" className="mx-auto w-full max-w-shell flex-1 px-3 py-4">
        <HeaderActionSuppressionProvider value={setActionSuppressed}>
          <Outlet />
        </HeaderActionSuppressionProvider>
      </main>

      <footer className="border-t border-hairline bg-white">
        <nav
          aria-label={t('footer.label')}
          className="mx-auto flex w-full max-w-shell flex-wrap items-center justify-center gap-x-2 gap-y-1 px-3 py-3 md:h-footer md:py-0"
        >
          <Link className="text-label-m text-ink-500 hover:underline" to={paths.static.privacy}>
            {t('footer.privacy')}
          </Link>
          <span aria-hidden="true" className="size-0.5 rounded-full bg-ink-300" />
          <Link className="text-label-m text-ink-500 hover:underline" to={paths.static.terms}>
            {t('footer.terms')}
          </Link>
          <span aria-hidden="true" className="size-0.5 rounded-full bg-ink-300" />
          <Link
            className="text-label-m text-ink-500 hover:underline"
            to={paths.static.accessibility}
          >
            {t('footer.accessibility')}
          </Link>
          <span aria-hidden="true" className="size-0.5 rounded-full bg-ink-300" />
          <Link className="text-label-m text-ink-500 hover:underline" to={paths.static.help}>
            {t('footer.help')}
          </Link>
        </nav>
      </footer>
    </div>
  )
}
